// The teams (joukkuevaraukset) backend, in one place. See kumpula/TEAMS.md.
//
// Every write in this feature goes through a callable on purpose: the quota claim must be
// transactional, the offer set must be decided server-side, and teams must reach dates that
// config/publishing.publicdates hides from ordinary customers. The client has NO Firestore
// access to any teams collection - the deployed ruleset denies everything - so nothing here has
// a direct-read fallback, by design.
//
// THE CALLABLE NAMES ARE NOT `test`-PREFIXED ON UTEST, unlike every other group in this app.
// There is no testteams-* mirror in falcon/functions: no functions/test/teams.js and no
// `exports.testteams` in index.js. The reflex `appconfig.istest ? 'testteams-x' : 'teams-x'`
// would therefore resolve to a function that does not exist, and the feature would be dead
// exactly where it gets tested. The test/ mirror exists to let staging BEHAVIOUR diverge, not to
// isolate data: both sets of functions run in the same project (falcon-328a1) and scope
// everything by customerid, so the production teams callables operate correctly on a utest
// venue's data. Precedent for unprefixed calls: users-resetPassword (LoginScreen),
// reservations-createChecksum (MatchReservationScreen), redis-salestatus (ReservationsScreen).
// Please do not "fix" this into the istest pattern.
import { useFirebase } from 'react-redux-firebase'

const REGION = 'europe-west1'

/**
 * Teams handlers answer HTTP 200 with `{ error: true, code, message }` on every business
 * refusal - `error` is the boolean true, not a string, and `message` is already the Finnish
 * sentence to show the user (falcon/functions/teams-utils.js:39-60). A rejected promise
 * therefore means network or infrastructure, never "no".
 *
 * Normalised to { ok, data, code, message } so no screen repeats the dance. `code: 'transport'`
 * carries no message - the screen supplies a translated one.
 */
const normalise = (response) => {
  const data = (response && response.data) || {}
  if (data.error) {
    return { ok: false, data, code: data.code || 'internal', message: data.message || '' }
  }
  return { ok: true, data, code: null, message: '' }
}

const transportFailure = (err) => ({
  ok: false,
  data: {},
  code: 'transport',
  message: '',
  err,
})

export function useTeamsApi({ appconfig }) {
  const firebase = useFirebase()

  const call = (name, payload) => {
    firebase.functions().region = REGION
    return firebase
      .functions()
      .httpsCallable('teams-' + name)(payload)
      .then(normalise)
      .catch(transportFailure)
  }

  // customerid is required by every callable except accept.
  const withCustomer = (payload) => ({
    ...(payload || {}),
    customerid: appconfig ? appconfig.accountkey : null,
  })

  return {
    // Everything screen 1 and 3 need, in one round trip: teams, seasons, formats, ties and the
    // quota block. The client cannot read any of it from Firestore, which is why it arrives
    // inline. Note this call also maintains the caller's acl.teammanager flag server-side, so it
    // is not a pure read - do not put it in a loop.
    managerTeams: () => call('managerteams', withCustomer()),

    // AVAILABILITY, NOT ARRANGEMENTS: contiguous free ranges per season slot and court, never a
    // collapsed list of permitted tie start times. The manager lays out the evening.
    // `tieid` narrows `rows` to what that tie may still book; omit it for the format's full offer.
    availability: ({ teamid, date, tieid }) =>
      call('availability', withCustomer({ teamid, date, tieid: tieid || null })),

    // Claims quota and mints the acceptance token. Books NOTHING - the manager has reserved the
    // right to book, not any courts.
    createTie: ({ teamid, date, opponent }) =>
      call('createtie', withCustomer({ teamid, date, opponent: opponent || '' })),

    // No duration, no end, no price, no teamid, no date - and that absence is the design. The
    // length comes from the format row `rubberid` names, the price from the season slot, the date
    // from `start`. If this ever seems to need a duration, it is missing a rubberid.
    bookRubber: ({ tieid, rubberid, resourceid, start }) =>
      call('bookrubber', withCustomer({ tieid, rubberid, resourceid, start })),

    // Returns the fresh remaining offer (with `count`, unlike availability.rows). Touches
    // neither the tie nor the quota.
    cancelRubber: ({ tieid, reservationid }) =>
      call('cancelrubber', withCustomer({ tieid, reservationid })),

    // Cancels every remaining rubber AND releases the quota. `cancelled` comes back as `false`
    // for an already-cancelled tie or as a COUNT otherwise - typeof it before rendering.
    cancelTie: ({ tieid }) => call('canceltie', withCustomer({ tieid })),

    // The whole season's availability in one call, for the overview list. Not built yet - see
    // kumpula/TEAMS_BACKEND_CHANGES.md section 3 - so a caller must handle the failure; there is
    // no older behaviour to fall back to. Defaults to the season's own window when from/to are
    // omitted.
    seasonAvailability: ({ teamid, from, to }) =>
      call('seasonavailability', withCustomer({ teamid, from: from || null, to: to || null })),

    // Mails the link list to the team's OWN MANAGERS (not to opponents - the system never
    // emails an opponent). No idempotency key: calling twice sends twice.
    sendLinks: ({ teamid, tieids }) =>
      call('sendlinks', withCustomer({ teamid, tieids: tieids || null })),
  }
}

/**
 * The opponent's acceptance call. Deliberately separate: it takes NO customerid and NO auth -
 * the token is the authorisation, and this is the only callable in falcon with no auth guard.
 *
 * Omit `opponentteam` to render the page; send it to claim the date. That presence is the
 * read/write discriminator, so a render must never pass an empty string through as a claim.
 *
 * Answers `{ state, ...view }` and never `{ error: true }` except on an internal fault, so it is
 * normalised only far enough to surface transport failures.
 */
export function useTeamsAccept() {
  const firebase = useFirebase()

  return ({ token, opponentteam }) => {
    firebase.functions().region = REGION
    const payload = { token }
    if (opponentteam && opponentteam.trim()) payload.opponentteam = opponentteam.trim()
    return firebase
      .functions()
      .httpsCallable('teams-accept')(payload)
      .then((response) => normalise(response))
      .catch(transportFailure)
  }
}
